"use client"
import { cn } from "@/lib/utils"

import { CloseIcon } from "@/icons"
import { forwardRef, useEffect, useState } from "react"
import { EyeMini, EyeSlashMini } from "@medusajs/icons"

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  labelClassName?: string
  icon?: React.ReactNode
  clearable?: boolean
  error?: boolean
  changeValue?: (value: string) => void
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    labelClassName,
    icon,
    clearable,
    className,
    error,
    changeValue,
    value,
    onChange,
    type,
    ...props
  },
  ref
) {
  const [showPassword, setShowPassword] = useState(false)
  const [inputType, setInputType] = useState(type)
  let paddingY = ""
  if (icon) paddingY += "pl-[46px] "
  if (clearable) paddingY += "pr-[38px]"

  useEffect(() => {
    if (type === "password" && showPassword) {
      setInputType("text")
    }

    if (type === "password" && !showPassword) {
      setInputType("password")
    }
  }, [type, showPassword])

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (changeValue) changeValue(e.target.value)
    onChange?.(e)
  }

  const clearHandler = () => {
    if (changeValue) changeValue("")
  }

  // Only force controlled mode when `value` is passed. `value ?? ""` made
  // every field controlled-empty so typing appeared broken with RHF setValue.
  const controlled = value !== undefined

  return (
    <label className={cn("label-md", labelClassName)}>
      {props.required && <span className="text-red-500 ml-1">*</span>}
      {label}
      <div className="relative mt-2">
        {icon && (
          <span className="absolute top-0 left-[16px] h-full flex items-center">
            {icon}
          </span>
        )}

        <input
          ref={ref}
          className={cn(
            "w-full h-[42px] px-[15px] py-[11px] border border-[#d1d5dc] rounded-[8px] bg-white text-[14px] text-[#1e2939] leading-[20px] placeholder:text-[#d1d5dc] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500",
            error && "border-negative focus:border-negative",
            props.disabled && "bg-disabled cursor-not-allowed",
            paddingY,
            className
          )}
          {...props}
          {...(controlled ? { value } : {})}
          onChange={changeHandler}
          type={inputType}
        />
        {clearable && controlled && value && (
          <span
            className="absolute h-full flex items-center top-0 right-[16px] cursor-pointer"
            onClick={clearHandler}
          >
            <CloseIcon />
          </span>
        )}
        {type === "password" && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="text-ui-fg-subtle px-4 focus:outline-none transition-all duration-150 outline-none focus:text-ui-fg-base absolute right-0 top-4"
          >
            {showPassword ? <EyeMini /> : <EyeSlashMini />}
          </button>
        )}
      </div>
    </label>
  )
})
